import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, X, User as UserIcon, ShieldAlert, Timer } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MANUAL_TAT_OPTIONS, tatHoursFor, isSuperAdmin } from "@/lib/roles";

const draftKey = (ref, action) => `vgws_remarks_draft_${ref}_${action}`;

/**
 * RemarksModal — reusable action-confirmation modal for the HOD & Admin
 * dashboards. Enforces minimum-10-char remarks client + server side, restores
 * accidental drafts from sessionStorage, autofocuses, supports Ctrl+Enter and
 * Esc, shows an applicant-preview card that updates live.
 *
 * Phase 2e:
 *  - Manual follow-up TAT dropdown (optional).
 *  - Super-admin override banner + confirmation step when acting on behalf
 *    of another approver.
 */
export default function RemarksModal({ open, onClose, onDone, requisition, action, accent = "maroon" }) {
  const { user } = useAuth();
  const [remarks, setRemarks] = useState("");
  const [tatKey, setTatKey] = useState("none");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [overrideAck, setOverrideAck] = useState(false);
  const textareaRef = useRef(null);

  const cfg = {
    approve: { label: "Approve", verb: "Approving", pastTense: "Approved", Icon: CheckCircle2, tone: accent, hint: "This remark will move the requisition forward." },
    on_hold: { label: "Place on Hold", verb: "Placing on hold", pastTense: "On-hold", Icon: PauseCircle, tone: "gold", hint: "The applicant will be asked to revise the requisition." },
    reject:  { label: "Reject", verb: "Rejecting", pastTense: "Rejected", Icon: XCircle, tone: "danger", hint: "The requisition will be closed with your reason." },
  }[action] || null;

  // Detect super_admin override: super_admin acting on a card that lists a
  // different assignee.
  const assignedTo = requisition?.hod_name || requisition?.applicant_name;
  const currentApprovers = requisition?.current_approvers || [];
  const isOverride = isSuperAdmin(user) && !currentApprovers.includes(user.id);

  // Reset when opening (or when switching which requisition/action the modal
  // targets). CRITICAL: do NOT depend on `cfg` — it's a plain object created
  // in the render body, so its reference changes every render, which used to
  // re-fire this effect on every keystroke and wipe out the TAT dropdown
  // selection + super-admin override ack. Depend on stable primitives only.
  const refCode = requisition?.ref_code;
  useEffect(() => {
    if (!open || !refCode) return;
    setError(""); setTouched(false); setOverrideAck(false); setTatKey("none");
    try {
      const cached = sessionStorage.getItem(draftKey(refCode, action));
      setRemarks(cached || "");
    } catch { setRemarks(""); }
    setTimeout(() => textareaRef.current?.focus(), 20);
  }, [open, refCode, action]);

  useEffect(() => {
    if (!open || !requisition) return;
    try {
      if (remarks) sessionStorage.setItem(draftKey(requisition.ref_code, action), remarks);
      else sessionStorage.removeItem(draftKey(requisition.ref_code, action));
    } catch {}
  }, [remarks, open, requisition, action]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
      else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, remarks, tatKey, overrideAck]);

  if (!open || !requisition || !cfg) return null;
  const trimmed = remarks.trim();
  // Phase 12.1: NO min-length rule on status remarks (approve/on_hold/reject).
  const remarksMandatory = false;
  const minOk = true;
  const showRequiredHint = false;

  const confirmClass = {
    maroon: "bg-[#7a1f2b] hover:bg-[#5a1520] border-[#5a1520]",
    teal:   "bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a]",
    gold:   "bg-[#b48a45] hover:bg-[#8a6a2f] border-[#8a6a2f]",
    danger: "bg-[#b3261e] hover:bg-[#8a1c17] border-[#8a1c17]",
  }[cfg.tone];

  const clearDraft = () => {
    try { sessionStorage.removeItem(draftKey(requisition.ref_code, action)); } catch {}
  };

  const submit = async () => {
    if (!minOk) { setTouched(true); return; }
    if (isOverride && !overrideAck) { setOverrideAck(true); return; }
    setBusy(true); setError("");
    try {
      const hours = tatHoursFor(tatKey);
      const payload = { action, remarks: trimmed };
      if (hours) payload.manual_tat_hours = hours;
      const { data } = await api.post(`/requisitions/${requisition.ref_code}/action`, payload);
      clearDraft();
      const toastCopy = {
        approve: "Approved — remarks recorded",
        on_hold: "Placed on hold — remarks recorded",
        reject:  "Rejected — remarks recorded",
      }[action] || `${cfg.pastTense} — remarks recorded`;
      toast.success(`${toastCopy} · ${requisition.ref_code}`, { duration: 4000 });
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally { setBusy(false); }
  };

  const showError = touched && !minOk;

  // Two-step override confirmation
  if (isOverride && !overrideAck) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
           data-testid="super-admin-override-modal" onClick={onClose}>
        <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
             onClick={(e) => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-[#e2d7c3] bg-[#faf5eb] flex items-center gap-2">
            <ShieldAlert className="text-[#b3261e]" size={20} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">Super Admin override</h3>
          </div>
          <div className="px-5 py-4 space-y-3">
            <p className="text-sm text-[#1f2320]">
              You are acting as <strong>Super Admin</strong> on behalf of{" "}
              <strong>{assignedTo || "the assigned approver"}</strong>. This will be recorded in
              the audit trail as <span className="font-mono text-xs bg-[#faf5eb] px-1.5 py-0.5 rounded">acted_on_behalf_of</span>.
              Continue?
            </p>
          </div>
          <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
            <button onClick={onClose} className="btn-ghost" data-testid="override-cancel">Cancel</button>
            <button onClick={() => setOverrideAck(true)}
                    className="btn-vgws"
                    data-testid="override-confirm">
              Continue as Super Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         data-testid="remarks-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <cfg.Icon size={20} className={
              cfg.tone === "danger" ? "text-[#b3261e]" :
              cfg.tone === "gold"   ? "text-[#b48a45]" :
              cfg.tone === "teal"   ? "text-[#2d6b6b]" : "text-[#7a1f2b]"
            } />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              {cfg.label} — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {isOverride && (
            <div className="text-xs text-white bg-[#7a1f2b] px-3 py-2 rounded flex items-start gap-2"
                 data-testid="super-admin-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong>Super Admin override.</strong> Acting on behalf of{" "}
                <strong>{assignedTo || "the assigned approver"}</strong>. This will be logged
                in the audit trail.
              </div>
            </div>
          )}

          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#b48a45] px-3 py-2 rounded">
            <strong>This remark will be visible to the applicant and all subsequent stages.</strong> {cfg.hint}
          </div>

          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">
              {error}
            </div>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks {showRequiredHint ? <span className="text-[#b3261e]">*</span> : <span className="text-[#6e7770] text-xs font-normal">(optional)</span>}
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">
                {showRequiredHint ? "min 2 · " : ""}Ctrl+Enter to confirm · Esc to close
              </span>
            </span>
            <textarea
              ref={textareaRef}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
              onBlur={() => setTouched(true)}
              rows={5}
              placeholder={remarksMandatory ? "Describe your reason clearly…" : "Add any note for the record (optional)…"}
              className={`input-vgws min-h-[120px] ${
                showError ? "!border-[#b3261e] ring-1 ring-[#b3261e]/30" :
                (remarksMandatory && minOk) ? "!border-[#2d6b6b] ring-1 ring-[#2d6b6b]/25" : ""
              }`}
              data-testid="remarks-textarea"
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={remarksMandatory && !minOk ? "text-[#b3261e]" : "text-[#2d6b6b]"} data-testid="remarks-error">
                {remarksMandatory
                  ? (minOk ? "Looks good." : "Remarks are mandatory (minimum 2 characters) for hold/reject.")
                  : "Remarks are optional for Approve. Add context if useful."}
              </span>
              <span className={`font-mono ${minOk ? "text-[#2d6b6b]" : "text-[#6e7770]"}`}>{trimmed.length} / 1000</span>
            </div>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5 inline-flex items-center gap-1">
              <Timer size={14} className="text-[#2d6b6b]" />
              Set follow-up TAT
              <span className="ml-1 text-[10px] uppercase tracking-widest text-[#6e7770]">optional · working hours</span>
            </span>
            <select
              value={tatKey}
              onChange={(e) => setTatKey(e.target.value)}
              className="input-vgws"
              data-testid="manual-tat-select"
            >
              {MANUAL_TAT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <div className="mt-1 text-[11px] text-[#6e7770]">
              A single reminder fires when the deadline is reached. No cascade, no escalation.
            </div>
          </label>

          {trimmed.length > 0 && (
            <div className="border border-dashed border-[#cbbba0] rounded p-3 bg-white" data-testid="remarks-preview">
              <div className="text-[10px] uppercase tracking-widest text-[#6e7770] font-semibold mb-1 flex items-center gap-1">
                <UserIcon size={10} /> Preview to {requisition.applicant_name}
              </div>
              <div className="text-sm">
                <div className="text-[#7a1f2b] font-semibold">{cfg.pastTense} at your current stage</div>
                <div className="text-[#1f2320] whitespace-pre-wrap mt-1">{trimmed}</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="remarks-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!minOk || busy}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm ${confirmClass} ${(!minOk || busy) ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="remarks-confirm"
          >
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <cfg.Icon size={16} />}
            {busy ? `${cfg.verb}…` : `Confirm ${cfg.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
